/**
 * Prompt Enhancer Service
 * Intelligently updates prompts based on active filters and selected model.
 */

import { ModelId } from './imageGeneration';

interface FilterSelections {
    [key: string]: string | undefined;
}
// Raw artist list source (newline separated)
import { ANIMAGINE_ARTISTS_SOURCE } from './data/animagineArtists';


const animagineArtistsMapping = ANIMAGINE_ARTISTS_SOURCE.split(/\r?\n/).filter(Boolean).reduce((acc, line) => {
    const rawName = line.trim();
    if (!rawName) return acc;

    // Normalize: Remove backslashes used for escaping (e.g. "name \(tag\)" -> "name (tag)")
    const cleanName = rawName.replace(/\\/g, '');

    // Create Tag: standard animagine formatting (spaces -> underscores, escape parens)
    // We use cleanName to ensure uniform escaping regardless of input format
    const tag = cleanName.replace(/ /g, '_').replace(/\(/g, '\\(').replace(/\)/g, '\\)');

    // Create Label: clean name but Title Cased (handles parens correctly)
    const label = cleanName.toLowerCase()
        .replace(/\(/g, ' (') // Ensure space before opening parenthesis
        .replace(/\s+/g, ' ') // Collapse text
        .trim()
        .replace(/(?:^|\s|["'([{])+\S/g, match => match.toUpperCase());

    acc[label] = tag; // Raw tag, enhancer will apply default 1.3 weight
    return acc;
}, {} as Record<string, string>);

// Mapping of UI values to Prompt additions
export const PROMPT_MAPPINGS: Record<string, Record<string, string>> = {
    // Lens / Shot Type (matches Generator.tsx options)
    shot: {
        'Macro Detail': 'macro photography, extreme close-up, detailed',
        'Product Zoom': 'close-up product shot, detailed, sharp focus',
        'Waist Up': 'waist-up shot, medium shot',
        'Wide View': 'wide angle lens, expansive view, full scene',
    },
    // Camera Angle (matches Generator.tsx options)
    angle: {
        'Eye Level': 'eye level angle',
        'From Above': 'high angle shot, looking down',
        'From Below': 'low angle shot, looking up, heroic',
        'Top Down': 'overhead shot, top-down// View first lay',
    },
    // Lighting (matches Generator.tsx options)
    lighting: {
        'Studio': 'studio lighting, professional lighting, softbox',
        'Natural': 'natural lighting, soft ambient light',
        'Cinematic': 'cinematic lighting, dramatic shadows, rim light',
        'Neon': 'neon lighting, cyberpunk aesthetic, vibrant colors',
        'Softbox': 'softbox lighting, even illumination, studio setup',
    },
    animagine_shot: {
        'Close-Up': 'close-up',
        'Portrait': 'portrait',
        'Upper Body': 'upper_body',
        'Cowboy Shot': 'cowboy_shot',
        'Full Body': 'full_body',
        'Wide Shot': 'wide_shot',
    },
    animagine_angle: {
        'Eye Contact': 'looking_at_viewer',
        'Looking Back': 'looking_back',
        'Profile': 'profile',
        'From Above': 'from_above',
        'From Below': 'from_below',
        'Dutch Angle': 'dutch_angle',
        'Fisheye': 'fisheye',
    },
    animagine_focus: {
        'Depth of Field': 'depth_of_field',
        'Bokeh': 'bokeh',
        'Foreground Focus': 'foreground_focus',
        'Motion Blur': 'motion_blur',
    },
    // Time of Day (matches Generator.tsx options)
    tod: {
        'Golden Hour': 'golden hour, warm sunlight, soft glow',
        'High Noon': 'noon, harsh sunlight, high contrast',
        'Blue Hour': 'blue hour, cold tones, twilight',
        'Midnight': 'night time, dark, moonlit, dramatic',
    },
    // Style (matches Generator.tsx options)
    style: {
        'Cinematic': 'cinematic style, movie still, dramatic lighting',
        '3D Render': '3d render, unreal engine 5, octane render',
        'Photorealistic': 'photorealistic, 8k, raw photo, hyperrealistic',
        'Anime': 'anime style, cell shaded, studio ghibli style',
        'Minimalist': 'minimalist, clean, simple composition',
    },
    // Animagine Specific Mappings
    quality: {
        'Masterpiece': 'masterpiece',
        'Best Quality': 'best quality',
        'Great Quality': 'great quality',
        'Good Quality': 'good quality',
        'Normal Quality': 'normal quality'
    },
    aesthetic: {
        'Very Aesthetic': 'very aesthetic',
        'Aesthetic': 'aesthetic',
        'Displeasing': 'displeasing'
    },
    era: {
        'Newest (2021+)': 'newest',
        'Recent (2018-20)': 'recent',
        'Mid (2015-17)': 'mid',
        'Early (2011-14)': 'early',
        'Oldest (2005-10)': 'oldest'
    },
    rating: {
        'Safe': 'safe',
        'Sensitive': 'sensitive',
        'NSFW': 'nsfw',
        'Explicit': 'explicit, nsfw'
    },
    // New Animagine Filters
    animagine_char: {
        'Female': '1girl, solo',
        'Male': '1boy, solo',
        'Duo (F/F)': '2girls',
        'Duo (M/M)': '2boys',
        'Duo (M/F)': '1boy, 1girl',
        'Group (F)': 'multiple_girls, group',
        'Group (M)': 'multiple_boys, group',
        'Group (M/F)': 'multiple_girls, multiple_boys, group',
        'Non-Human': 'no_humans'
    },
    animagine_attire: {
        // Headwear
        'Hat': 'hat', 'Cap': 'cap', 'Baseball Cap': 'baseball_cap', 'Beanie': 'beanie', 'Beret': 'beret',
        'Fedora': 'fedora', 'Top Hat': 'top_hat', 'Cowboy Hat': 'cowboy_hat', 'Witch Hat': 'witch_hat',
        'Helmet': 'helmet', 'Visored Helmet': 'visored_helmet',
        'Hair Ribbon': 'hair_ribbon', 'Hair Bow': 'hair_bow', 'Hairband': 'hairband', 'Headband': 'headband',
        'Hair Flower': 'hair_flower', 'Hair Ornament': 'hair_ornament', 'Kanzashi': 'kanzashi',
        // Tops
        'T-Shirt': 't-shirt', 'Shirt': 'shirt', 'Blouse': 'blouse', 'Tank Top': 'tank_top',
        'Camisole': 'camisole', 'Tube Top': 'tube_top', 'Crop Top': 'crop_top',
        'Hoodie': 'hoodie', 'Sweater': 'sweater', 'Cardigan': 'cardigan',
        'Blazer': 'blazer', 'Jacket': 'jacket', 'Coat': 'coat', 'Trench Coat': 'trench_coat',
        'Cape': 'cape', 'Cloak': 'cloak', 'Robe': 'robe',
        'Kimono': 'kimono', 'Yukata': 'yukata', 'Haori': 'haori',
        // Bottoms
        'Skirt': 'skirt', 'Miniskirt': 'miniskirt', 'Micro Skirt': 'micro_skirt', 'Pleated Skirt': 'pleated_skirt',
        'Pencil Skirt': 'pencil_skirt', 'Long Skirt': 'long_skirt', 'Bubble Skirt': 'bubble_skirt',
        'Pants': 'pants', 'Jeans': 'jeans', 'Denim': 'denim', 'Shorts': 'shorts', 'Denim Shorts': 'denim_shorts',
        'Leggings': 'leggings', 'Yoga Pants': 'yoga_pants', 'Sweatpants': 'sweatpants',
        'Bloomers': 'bloomers', 'Dolphin Shorts': 'dolphin_shorts', 'Hakama': 'hakama',
        // Legwear
        'Socks': 'socks', 'Ankle Socks': 'ankle_socks', 'Kneehighs': 'kneehighs',
        'Over-Kneehighs': 'over-kneehighs', 'Thighhighs': 'thighhighs',
        'Pantyhose': 'pantyhose', 'Tights': 'tights', 'Fishnets': 'fishnets',
        'Garter Straps': 'garter_straps', 'Zettai Ryouiki': 'zettai_ryouiki',
        // Footwear
        'Shoes': 'shoes', 'Sneakers': 'sneakers', 'Boots': 'boots', 'Knee Boots': 'knee_boots',
        'Thigh Boots': 'thigh_boots', 'Combat Boots': 'combat_boots',
        'Loafers': 'loafers', 'Mary Janes': 'mary_janes',
        'High Heels': 'high_heels', 'Pumps': 'pumps', 'Stilettos': 'stilettos',
        'Sandals': 'sandals', 'Flip-Flops': 'flip-flops', 'Barefoot': 'barefoot',
        // Accessories
        'Glasses': 'glasses', 'Sunglasses': 'sunglasses', 'Goggles': 'goggles', 'Monocle': 'monocle', 'Eyepatch': 'eyepatch',
        'Jewelry': 'jewelry', 'Necklace': 'necklace', 'Earrings': 'earrings', 'Bracelet': 'bracelet', 'Choker': 'choker', 'Ring': 'ring',
        'Gloves': 'gloves', 'Fingerless Gloves': 'fingerless_gloves', 'Elbow Gloves': 'elbow_gloves',
        'Scarf': 'scarf', 'Neckerchief': 'neckerchief', 'Necktie': 'necktie', 'Bowtie': 'bowtie',
    },
    animagine_physique: {
        // Body Type
        'Slim': 'slim', 'Skinny': 'skinny', 'Slender': 'slender',
        'Muscular': 'muscular', 'Toned': 'toned', 'Abs': 'abs', 'Defined Abs': 'defined_abs', 'Muscular Female': 'muscular_female',
        'Plump': 'plump', 'Chubby': 'chubby', 'Curvy': 'curvy', 'Voluptuous': 'voluptuous',
        'Wide Hips': 'wide_hips', 'Thick Thighs': 'thick_thighs',
        'Tall': 'tall', 'Petite': 'petite', 'Short': 'short_stature',
        'Flat Chest': 'flat_chest', 'Small Breasts': 'small_breasts', 'Medium Breasts': 'medium_breasts',
        'Large Breasts': 'large_breasts', 'Huge Breasts': 'huge_breasts', 'Gigantic Breasts': 'gigantic_breasts',
    },
    animagine_creature: {
        // Animal Features
        'Cat Ears': 'cat_ears', 'Fox Ears': 'fox_ears', 'Bunny Ears': 'bunny_ears', 'Elf Ears': 'pointy_ears, elf',
        // Wings
        'Angel Wings': 'angel_wings', 'Demon Wings': 'demon_wings', 'Fairy Wings': 'fairy_wings',
        // Horns/Tails
        'Demon Horns': 'demon_horns', 'Oni Horns': 'oni_horns',
        'Demon Tail': 'demon_tail', 'Fluffy Tail': 'fluffy_tail',
        // Other
        'Scales': 'scales', 'Centaur': 'centaur', 'Mermaid': 'mermaid',
    },
    animagine_hair: {
        // Length
        'Pixie Cut': 'very_short_hair', 'Short Hair': 'short_hair', 'Medium Hair': 'medium_hair',
        'Long Hair': 'long_hair', 'Very Long Hair': 'very_long_hair', 'Absurdly Long Hair': 'absurdly_long_hair',
        // Colors (order: purple, blue, green, yellow, orange, brown, red, pink, black, white, grey, blonde)
        'Purple Hair': 'purple_hair', 'Blue Hair': 'blue_hair', 'Green Hair': 'green_hair',
        'Yellow Hair': 'yellow_hair', 'Orange Hair': 'orange_hair', 'Brown Hair': 'brown_hair',
        'Red Hair': 'red_hair', 'Pink Hair': 'pink_hair',
        'Black Hair': 'black_hair', 'White Hair': 'white_hair', 'Grey Hair': 'grey_hair', 'Blonde Hair': 'blonde_hair',
        // Color Effects
        'Gradient Hair': 'gradient_hair', 'Two-Tone Hair': 'two-tone_hair', 'Inner Color': 'colored_inner_hair',
        'Streaked Hair': 'streaked_hair', 'Rainbow Hair': 'multicolored_hair, rainbow_hair',
        // Bangs
        'Blunt Bangs': 'blunt_bangs', 'Swept Bangs': 'swept_bangs', 'Hair Between Eyes': 'hair_between_eyes',
        'Hair Over Eye': 'hair_over_one_eye', 'Blindfold Hair': 'blindfold_hair',
        // Styles
        'Ponytail': 'ponytail', 'Side Ponytail': 'side_ponytail', 'High Ponytail': 'high_ponytail', 'Low Ponytail': 'low_ponytail',
        'Twintails': 'twintails', 'Low Twintails': 'low_twintails', 'Short Twintails': 'short_twintails',
        'Braid': 'braid', 'Single Braid': 'single_braid', 'Twin Braids': 'twin_braids', 'Crown Braid': 'crown_braid', 'French Braid': 'french_braid',
        'Hair Bun': 'hair_bun', 'Double Bun': 'double_bun', 'Braided Bun': 'braided_bun',
        // Texture
        'Straight Hair': 'straight_hair', 'Wavy Hair': 'wavy_hair', 'Curly Hair': 'curly_hair', 'Ringlets': 'ringlets',
        'Spiked Hair': 'spiked_hair', 'Messy Hair': 'messy_hair', 'Disheveled Hair': 'disheveled_hair',
        'Ahoge': 'ahoge', 'Floating Hair': 'floating_hair',
    },
    animagine_eyes: {
        // Colors
        'Purple Eyes': 'purple_eyes', 'Blue Eyes': 'blue_eyes', 'Green Eyes': 'green_eyes',
        'Yellow Eyes': 'yellow_eyes', 'Orange Eyes': 'orange_eyes', 'Red Eyes': 'red_eyes', 'Brown Eyes': 'brown_eyes',
        // Shape/Style
        'Tsurime': 'tsurime', 'Tareme': 'tareme', 'Slit Pupils': 'slit_pupils',
        'Flower Pupils': 'flower-shaped_pupils', 'Heart Pupils': 'heart-shaped_pupils',
        'Star Pupils': 'star-shaped_pupils', 'Symbol Pupils': 'symbol-shaped_pupils',
        // States
        'Closed Eyes': 'closed_eyes', 'Winking': 'one_eye_closed', 'Half-Closed Eyes': 'half-closed_eyes',
        'Empty Eyes': 'empty_eyes', 'Glowing Eyes': 'glowing_eyes',
    },
    animagine_emotion: {
        // Happy
        'Smile': 'smile', 'Open Mouth': 'open_mouth', 'Laughing': 'laughing', 'Grin': 'grin',
        'V-Brows': 'v-shaped_eyebrows', 'Blush': 'blush', 'Blush Lines': 'blush_stickers',
        'Heart Eyes': 'heart_in_eyes', 'Sparkles': 'sparkles', 'Wide-Eyed': 'wide_eyed',
        // Sad
        'Crying': 'crying', 'Tears': 'tears', 'Frown': 'frown', 'Sad': 'sad', 'Gloomy': 'gloomy', 'Depressed': 'depressed',
        // Angry
        'Angry': 'angry', 'Annoyed': 'annoyed', 'Furrowed Brow': 'furrowed_brow', 'Clenched Teeth': 'clenched_teeth', 'Shouting': 'shouting',
        // Scared
        'Scared': 'scared', 'Terror': 'terror', 'Screaming': 'screaming', 'Pale Face': 'pale',
        // Other
        'Seductive Smile': 'seductive_smile', 'Licking Lips': 'licking_lips', 'Heavy Breathing': 'heavy_breathing',
        'Tongue Out': 'tongue_out', 'Wink': 'wink',
        'Embarrassed': 'embarrassed', 'Hiding Face': 'hiding_face', 'Looking Away': 'looking_away',
        'Expressionless': 'expressionless', 'Pout': 'pout', 'Sleeping': 'sleeping', 'Yawning': 'yawning',
    },
    animagine_style: {
        // Digital
        'Digital Painting': 'digital_painting', 'Digital Media': 'digital_media', 'CGI': 'cgi', '3D Render': '3d, render',
        'Blender': 'blender_medium',
        // Traditional
        'Traditional': 'traditional_media', 'Watercolor': 'watercolor_medium', 'Oil Painting': 'oil_painting_medium',
        'Ink': 'ink', 'Graphite': 'graphite_medium', 'Pencil Sketch': 'pencil_sketch', 'Charcoal': 'charcoal_drawing',
        // Vector/Line
        'Vector': 'vector_trace', 'Lineart': 'lineart', 'Flat Color': 'flat_color', 'Cel Shading': 'cel_shading',
        // Pixel
        'Pixel Art': 'pixel_art', '16-bit': '16-bit', 'Dithering': 'dithering',
        // Era/Aesthetic
        '80s Style': '1980s_style', '90s Style': '1990s_style', 'Retro': 'retro_artstyle',
        'Gothic': 'gothic', 'Kawaii': 'kawaii', 'Cyberpunk': 'cyberpunk', 'Steampunk': 'steampunk', 'Art Nouveau': 'art_nouveau',
        // Color
        'Monochrome': 'monochrome', 'Greyscale': 'greyscale', 'Sepia': 'sepia',
    },
    animagine_activity: {
        'Standing': 'standing',
        'Sitting': 'sitting',
        'Kneeling': 'kneeling',
        'Lying Down': 'lying',
        'Walking': 'walking',
        'Running': 'running',
        'Fighting': 'fighting, dynamic pose, action',
        'Dancing': 'dancing',
        'Eating': 'eating',
        'Sleeping': 'sleeping'
    },
    animagine_location: {
        'Simple Background': 'simple_background',
        'White Background': 'white_background',
        'Transparent Background': 'transparent_background',
        'Gradient Background': 'gradient_background',
        'Detailed Background': 'detailed_background',
        'Bedroom': 'bedroom',
        'Living Room': 'living_room',
        'Kitchen': 'kitchen',
        'Bathroom': 'bathroom',
        'Foyer': 'foyer',
        'Tatami Room': 'tatami',
        'Classroom': 'classroom',
        'School Corridor': 'school_corridor',
        'Gymnasium': 'gymnasium',
        'Library': 'library',
        'Science Room': 'science_room',
        'Shop': 'shop',
        'Convenience Store': 'convenience_store',
        'Restaurant': 'restaurant',
        'Cafe': 'cafe',
        'Bar': 'bar',
        'Dungeon': 'dungeon',
        'Castle': 'castle',
        'Throne Room': 'throne_room',
        'Spaceship': 'spaceship',
        'Laboratory': 'laboratory',
        'Forest': 'forest',
        'Woods': 'woods',
        'Tree': 'tree',
        'Flower Field': 'flower_field',
        'Meadow': 'meadow',
        'Garden': 'garden',
        'Jungle': 'jungle',
        'Beach': 'beach',
        'Ocean': 'ocean',
        'Lake': 'lake',
        'River': 'river',
        'Underwater': 'underwater',
        'City': 'city',
        'Cityscape': 'cityscape',
        'Street': 'street',
        'Alley': 'alley',
        'Rooftop': 'rooftop',
        'Park': 'park',
        'Train Station': 'train_station',
        'Ruins': 'ruins',
        'Desert': 'desert',
        'Wasteland': 'wasteland',
        'Snowy Landscape': 'snowy_landscape',
        'Blue Sky': 'blue_sky',
        'Cloudy Sky': 'cloudy_sky',
        'Starry Sky': 'starry_sky',
        'Night Sky': 'night_sky',
        'Moon': 'moon',
        'Full Moon': 'full_moon',
        'Fireworks': 'fireworks',
    },
    animagine_lighting: {
        'Cinematic': 'cinematic_lighting',
        'Volumetric': 'volumetric_lighting',
        'Backlighting': 'backlighting',
        'Rim Lighting': 'rim_lighting',
        'Soft Lighting': 'soft_lighting',
        'Bioluminescence': 'bioluminescence',
        'Neon Lights': 'neon_lights',
    },
    animagine_weather: {
        'Sunny': 'sunny',
        'Rain': 'rain',
        'Snowing': 'snowing',
        'Storm': 'storm',
        'Fog': 'fog',
        'Windy': 'windy',
    },
    animagine_time: {
        'Day': 'day',
        'Noon': 'noon',
        'Sunset': 'sunset',
        'Twilight': 'twilight',
        'Dusk': 'dusk',
        'Night': 'night',
        'Midnight': 'midnight',
    },
    animagine_skin: {
        'Pale': 'pale_skin',
        'White': 'white_skin',
        'Tan': 'tan',
        'Tan Lines': 'tan_lines',
        'Dark': 'dark_skin',
        'Very Dark': 'very_dark_skin',
        'Blue': 'blue_skin',
        'Green': 'green_skin',
        'Red': 'red_skin',
        'Grey': 'grey_skin',
        'Pale Blue': 'pale_blue_skin',
        'Purple': 'purple_skin',
    },
    animagine_artist: animagineArtistsMapping
};

/**
 * Enhances the prompt with active filters
 */
export function enhancePrompt(
    basePrompt: string,
    selections: FilterSelections,
    modelId: ModelId = 'prunaai/z-image-turbo',
    weights: Record<string, number | undefined> = {}
): string {

    // Helper to apply weight to a tag string
    const applyWeight = (tag: string, key: string): string => {
        const weight = weights[key];
        if (!weight) return tag;
        // Split by comma, wrap each part with weight
        return tag.split(', ').map(t => `(${t.trim()}:${weight})`).join(', ');
    };

    let modifiers: string[] = [];
    let prefixes: string[] = [];

    // Keys to check
    const standardKeys = ['subject', 'location', 'style', 'shot', 'angle', 'tod', 'lighting'];
    const animagineKeys = ['quality', 'aesthetic', 'era', 'rating'];
    const allKeys = [...standardKeys, ...animagineKeys];

    allKeys.forEach(key => {
        const value = selections[key];
        if (!value || value === 'None' || value === 'Default') return;

        let mappedVal = value;
        if (PROMPT_MAPPINGS[key] && PROMPT_MAPPINGS[key][value]) {
            mappedVal = PROMPT_MAPPINGS[key][value];
        }

        // Apply weight if present
        mappedVal = applyWeight(mappedVal, key);

        // Logic for placement
        if (key === 'quality' || key === 'aesthetic') {
            prefixes.push(mappedVal);
        } else {
            modifiers.push(mappedVal);
        }
    });

    // Default behavior (usually appends)
    const parts = [basePrompt];
    // Flux doesn't strictly need prefixes, but if we have them (e.g. style), we can append or prepend.
    // Let's put prefixes at start for consistency.
    if (prefixes.length > 0) parts.unshift(prefixes.join(', '));
    if (modifiers.length > 0) parts.push(modifiers.join(', '));

    return parts.join(', ');
}

/**
 * Extract Aspect Ratio string to API format
 */
export function getAspectRatio(selections: FilterSelections): string | undefined {
    const ratio = selections['ratio'];
    if (!ratio || ratio === 'Default' || ratio === 'None') return undefined;

    // Map common inputs and UI labels to strict API types
    const map: Record<string, string> = {
        '1:1': '1:1',
        'Square': '1:1',
        'Square (1:1)': '1:1',
        '16:9': '16:9',
        'Cinematic': '16:9',
        'Landscape': '16:9',
        'Landscape (16:9)': '16:9',
        'Widescreen (16:9)': '16:9',
        '9:16': '9:16',
        'Mobile': '9:16',
        'Mobile (9:16)': '9:16',
        'Portrait': '9:16',
        'Portrait (9:16)': '9:16',
        '21:9': '21:9',
        'Cinematic (21:9)': '21:9',
        '9:21': '9:21',
        'Vertical Cinema (9:21)': '9:21',
        'Tall (9:21)': '9:21',
        '4:3': '4:3',
        'Classic (4:3)': '4:3',
        '3:4': '3:4',
        'Vertical (3:4)': '3:4',
        'Classic Portrait (3:4)': '3:4',
        '3:2': '3:2',
        'Photo (3:2)': '3:2',
        '2:3': '2:3',
        'Vertical Photo (2:3)': '2:3',
        'Photo Portrait (2:3)': '2:3',
        '4:5': '4:5',
        'Social (4:5)': '4:5',
        '5:4': '5:4',
        'Vertical Social (5:4)': '5:4',

        // Animagine Specific Ratios
        '9:7': '9:7',
        'Standard (9:7)': '9:7',
        '7:9': '7:9',
        'Vertical (7:9)': '7:9',
        // 19:13 and 13:19 map to 3:2 and 2:3 for Animagine compatibility
        '19:13': '3:2',
        'Landscape (19:13)': '3:2',
        '13:19': '2:3',
        'Portrait (13:19)': '2:3',
        '7:4': '7:4',
        'Wide (7:4)': '7:4',
        '4:7': '4:7',
        'Tall (4:7)': '4:7',
        '12:5': '12:5',
        'Panorama (12:5)': '12:5',
        '5:12': '5:12',
        'Vertical Panorama (5:12)': '5:12',
        'Vertical Pan (5:12)': '5:12',
    };

    return map[ratio] as any || undefined;
}

/**
 * Parse a prompt string and extract matching filter selections
 * This is useful for auto-detecting filters from prompts that don't have stored selections
 */
export function parsePromptToSelections(prompt: string): Record<string, string | string[]> {
    const selections: Record<string, string | string[]> = {};
    const promptLower = prompt.toLowerCase();

    // Multi-select filter keys (these accumulate multiple values)
    const multiSelectKeys = ['animagine_attire', 'animagine_physique', 'animagine_creature',
        'animagine_hair', 'animagine_eyes', 'animagine_emotion', 'animagine_style', 'animagine_artist'];

    // Check each mapping category
    for (const [category, mappings] of Object.entries(PROMPT_MAPPINGS)) {
        const isMultiSelect = multiSelectKeys.includes(category);
        const matches: string[] = [];

        for (const [uiLabel, tagValue] of Object.entries(mappings)) {
            // Check if any of the tags (comma-separated) are in the prompt
            const tags = tagValue.split(', ').map(t => t.trim().toLowerCase());
            const found = tags.some(tag => {
                // Check for exact tag match (with word boundaries)
                const regex = new RegExp(`\\b${tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/_/g, '[_\\s]?')}\\b`, 'i');
                return regex.test(promptLower);
            });

            if (found) {
                if (isMultiSelect) {
                    matches.push(uiLabel);
                } else {
                    // Single select - take first match
                    if (!selections[category]) {
                        selections[category] = uiLabel;
                    }
                }
            }
        }

        if (isMultiSelect && matches.length > 0) {
            selections[category] = matches;
        }
    }

    return selections;
}
